import { contentTypeToMediaType, DEFAULT_TOLERANCE, MEDIA_TYPE } from 'constants/MediaConstants';

/**
 * Encode a file to base64 string
 * @param {File} file - The file to encode
 * @returns {Promise<string>} A promise that resolves to the base64 encoded string
 */
export const encodeFileToBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = readerEvt => {
      let data;
      if (!readerEvt) {
        data = reader.content;
      } else {
        data = readerEvt.target.result;
      }

      const base64 = btoa(data);
      resolve(base64);
    };
    reader.onerror = error => {
      reject(error);
    };
    reader.readAsBinaryString(file);
  });

/**
 * @typedef Dimension
 * @property {number} width image width.
 * @property {number} height image height.
 */

/**
 * Return image dimension
 * @return {Promise<Dimension>} A promise for the image dimension.
 */
export const getImageDimension = file =>
  // eslint-disable-next-line consistent-return
  new Promise((resolve, reject) => {
    try {
      const img = new Image();

      img.src = window.URL.createObjectURL(file);

      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;

        window.URL.revokeObjectURL(img.src);

        return resolve({ width, height });
      };
    } catch (e) {
      // eslint-disable-next-line no-promise-executor-return
      return reject(e);
    }
  });

/**
 * @param {Dimension} dimension
 * @param {number} ratio
 * @param {number} tolerance - Percent between 0 and 100
 * @returns {boolean}
 */
export const hasRatio = ({ width, height }, ratio, tolerance = DEFAULT_TOLERANCE) => {
  const minWidth = Math.round(height * ratio * (1 - tolerance / 100));
  const maxWidth = Math.round(height * ratio * (1 + tolerance / 100));
  return width >= minWidth && width <= maxWidth;
};

/**
 * Get the media type from config based on file type
 * @param {File} file - The file to check
 * @param {Object} config - RefData configuration object
 * @returns {string} The media type key
 */
const getMediaType = (file, config) => {
  const [mediaType] = Object.entries(config).find(([, { 'supported-types': supportedTypes }]) =>
    supportedTypes.includes(file.type));
  return mediaType;
};

/**
 * Check if file has correct dimensions according to config
 * @param {File} file - The file to validate
 * @param {Object} config - RefData configuration object
 * @returns {Promise<boolean>} True if dimensions are valid
 */
const hasRightDimensions = async (file, config) => {
  const type = getMediaType(file, config);
  if (config[type]) {
    const dimension = await getImageDimension(file);
    const hasRightWidth =
      dimension.width >= (config[type]['min-width'] ?? 0) && dimension.width <= (config[type]['max-width'] ?? Infinity);
    const hasRightHeight =
      dimension.height >= (config[type]['min-height'] ?? 0) &&
      dimension.height <= (config[type]['max-height'] ?? Infinity);
    return hasRightWidth && hasRightHeight;
  }
  return true;
};

/**
 * Media validation functions
 * @type {Object<string, Function>}
 */
export const MEDIA_VALIDATORS = {
  /**
   * Dimension validator factory
   * @param {Object} config - RefData configuration object
   * @param {import("react-intl").IntlShape} intl - Internationalization object for formatting messages
   * @returns {Function} Async validator function that returns error message or undefined
   */
  dimension:
    (config, intl) =>
    /** @param {File} file  */
      async file => {
        const mediaType = getMediaType(file, config);
        // Scope validation to images only
        if (contentTypeToMediaType(mediaType) !== MEDIA_TYPE.IMAGE) {
          return undefined;
        }

        const isValidDimensions = await hasRightDimensions(file, config);
        if (!isValidDimensions) {
          const { 'max-width': maxWidth, 'max-height': maxHeight, 'min-width': minWidth, 'min-height': minHeight } = config[mediaType];

          return intl.formatMessage(
            {
              id: 'mediaLibrary.errorDimension',
              defaultMessage: 'The file has invalid dimensions.{hasMinValidation, select, true { The minimum allowed dimensions are {minWidth}x{minHeight}px.} other {}}{hasMaxValidation, select, true { The maximum allowed dimensions are {maxWidth}x{maxHeight}px.} other {}}',
            },
            {
              minWidth,
              minHeight,
              maxWidth,
              maxHeight,
              hasMinValidation: Boolean(minWidth || minHeight),
              hasMaxValidation: Boolean(maxWidth || maxHeight),
            }
          );
        }
        return undefined;
      },
};

/**
 * Get additional media validators based on allowed configuration
 * @param {import("hooks/useMediaRules").AllowedMedias} allowed
 * @returns {string[]} Array of validator names that exist in MEDIA_VALIDATORS
 */
export const getAdditionalMediaValidators = (allowed = {}) =>
  allowed.validators?.reduce((acc, validator) => {
    if (MEDIA_VALIDATORS[validator]) {
      acc.push(validator);
    }
    return acc;
  }, []) ?? [];
